import { Shield, CheckCircle, X } from 'lucide-react';
import React from 'react';

import { PASSWORD_MESSAGES } from '../constants/password';
import { PasswordStrength } from '../utils/passwordValidation';

interface PasswordStrengthIndicatorProps {
  strength: PasswordStrength;
  showStrength: boolean;
}

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({ strength, showStrength }) => {
  if (!showStrength) {
    return null;
  }

  const getLevelColor = (level: PasswordStrength['level']) => {
    switch (level) {
      case 'Strong':
        return 'text-onboarding-success';
      case 'Good':
        return 'text-onboarding-success/60';
      case 'Fair':
        return 'text-brand-orange';
      default:
        return 'text-loss';
    }
  };

  const getLevelTextColor = (level: PasswordStrength['level']) => {
    switch (level) {
      case 'Strong':
        return 'text-onboarding-success';
      case 'Good':
        return 'text-onboarding-success/60';
      case 'Fair':
        return 'text-brand-orange';
      default:
        return 'text-loss';
    }
  };

  return (
    <div
      className="mt-3 rounded-[12px] border border-[var(--line)] bg-[var(--sunken)] p-4"
      role="status"
      aria-live="polite"
      aria-label={`Password strength: ${strength.level}`}
    >
      <div className="mb-3 flex justify-between">
        <span className="text-sm font-semibold text-[var(--ink)]">Password Strength</span>
        <div className="flex items-center space-x-2">
          <Shield className={`h-4 w-4 ${getLevelColor(strength.level)}`} />
          <span className={`text-sm font-medium ${getLevelTextColor(strength.level)}`}>{strength.level}</span>
        </div>
      </div>

      <div className="relative mb-3 h-2 rounded bg-[var(--line-2)]">
        <div
          className={`absolute left-0 top-0 h-2 rounded-full ${strength.color} transition-all duration-300`}
          style={{ width: `${strength.score}%` }}
          role="progressbar"
          aria-valuenow={strength.score}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={`Password strength score: ${strength.score} out of 100`}
        />
        <span className="absolute right-0 top-3 text-xs text-[var(--ink-3)]">{strength.score}/100</span>
      </div>

      {strength.feedback.length > 0 && (
        <div className="mb-3">
          <p className="mb-2 text-xs font-medium text-[var(--ink)]">Requirements:</p>
          <div className="grid gap-1">
            {strength.feedback.map((item: string, idx: number) => (
              <div key={idx} className="flex items-start space-x-2">
                <X className="mt-0.5 h-3 w-3 flex-shrink-0 text-loss" />
                <span className="text-xs leading-tight text-[var(--ink-3)]">{item}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {strength.level === 'Strong' && strength.feedback.length === 0 && (
        <div className="flex items-center space-x-2 mt-7 p-3 bg-onboarding-success-light rounded-lg border border-onboarding-success/30">
          <CheckCircle className="h-4 w-4 text-onboarding-success" />
          <span className="text-sm font-medium text-onboarding-success">{PASSWORD_MESSAGES.EXCELLENT}</span>
        </div>
      )}
    </div>
  );
};
